
import React, { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

import {
    SimpleGrid,
    Stack,
    Flex,
    InputGroup,
} from "@chakra-ui/react";

import { Search } from "lucide-react";

import ReusableInput from "components/Inputs/ReusableInput";
import NoDataFound from "components/NoDataGraphics/NoDataFound";

// Reuse existing components
import DriverPartnerSupportCard from "../DriverPartnerSupport/components/DriverPartnerSupportCard";
import UpdateSupportTicketDrawer from "../DriverPartnerSupport/components/UpdateSupportTicketDrawer";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    fetchAgentSupportTicketsAction,
    updateAgentSupportTicketAction,
} from "state/actions/agentSupportActions";

const AgentSupportTab = () => {
    const dispatch = useDispatch();

    const {
        tickets,
        loading,
    } = useSelector((state) => state.agentSupport);

    const supportTickets = Array.isArray(tickets?.data)
        ? tickets.data
        : Array.isArray(tickets)
        ? tickets
        : [];

    const [searchTerm, setSearchTerm] = useState("");
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [isViewDrawerOpen, setViewDrawerOpen] = useState(false);

    useEffect(() => {
        dispatch(fetchAgentSupportTicketsAction(1, 10));
    }, [dispatch]);

    const handleOpenDrawer = (ticket) => {
        setSelectedTicket(ticket);
        setViewDrawerOpen(true);
    };

    const handleCloseDrawer = () => {
        setSelectedTicket(null);
        setViewDrawerOpen(false);
    };

    const handleUpdateTicket = async (ticketId, payload) => {
        const response = await dispatch(
            updateAgentSupportTicketAction(ticketId, payload)
        );

        if (response?.success) {
            await dispatch(fetchAgentSupportTicketsAction(1, 10));
            handleCloseDrawer();
        }

        return response;
    };

    const search = searchTerm.toLowerCase().trim();

    const filteredTickets = supportTickets.filter((ticket) => {
        return (
            ticket.subject?.toLowerCase().includes(search) ||
            ticket.category?.toLowerCase().includes(search) ||
            ticket.status?.toLowerCase().includes(search) ||
            ticket.first_name?.toLowerCase().includes(search) ||
            ticket.last_name?.toLowerCase().includes(search) ||
            ticket.email?.toLowerCase().includes(search)
        );
    });

    return (
        <Stack spacing={5} mt={20}>
            <Flex
                justifyContent="space-between"
                alignItems="center"
            >
                {loading ? (
                    <Skeleton
                        width="300px"
                        height="40px"
                        borderRadius="12px"
                        style={{ marginLeft: 20 }}
                    />
                ) : (
                    <InputGroup
                        width="300px"
                        ml={5}
                        borderRadius="12px"
                    >
                        <ReusableInput
                            icon={
                                <Search
                                    size={20}
                                    color="#A0AEC0"
                                />
                            }
                            placeholder="Search agent tickets..."
                            value={searchTerm}
                            onChange={(e) =>
                                setSearchTerm(e.target.value)
                            }
                        />
                    </InputGroup>
                )}
            </Flex>

            <SimpleGrid
                columns={{
                    base: 1,
                    md: 2,
                    lg: 3,
                    xl: 4,
                }}
                spacing={5}
                p={5}
            >
                {loading && supportTickets.length === 0
                    ? Array.from({ length: 4 }).map((_, index) => (
                        <DriverPartnerSupportCard
                            key={index}
                            loading={true}
                        />
                    ))
                    : filteredTickets.map((ticket) => (
                        <DriverPartnerSupportCard
                            key={ticket.ticket_id}
                            {...ticket}
                            loading={false}
                            onView={() => handleOpenDrawer(ticket)}
                        />
                    ))}
            </SimpleGrid>

            {!loading && filteredTickets.length === 0 && (
                <Flex
                    justifyContent="center"
                    alignItems="center"
                    height="300px"
                    width="100%"
                >
                    <NoDataFound
                        message="No agent support tickets found."
                    />
                </Flex>
            )}

            {selectedTicket && (
                <UpdateSupportTicketDrawer
                    isOpen={isViewDrawerOpen}
                    onClose={handleCloseDrawer}
                    ticket={selectedTicket}
                    onUpdateTicket={handleUpdateTicket}
                />
            )}
        </Stack>
    );
};

export default AgentSupportTab;
